import { useEffect, useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { useInertiaCable } from "@inertia-cable/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Button } from "@/components/ui/button"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import { HelpHint } from "@/components/ui/help-hint"
import { FaqEntryList } from "@/components/domain/faq-entry-list"
import { FaqEntryForm } from "@/components/domain/faq-entry-form"
import type { FaqEntry, FaqEntryAgent } from "@/types/faq-entry"

type Props = {
  agents: FaqEntryAgent[]
  selectedAgentId: number
  faqEntries: FaqEntry[]
  topicSuggestions: string[]
  keywordSuggestions: string[]
  faq_entries_stream: string
}

const DRAFT_ID = -1

export default function FaqEntriesIndex({
  agents, selectedAgentId, faqEntries,
  topicSuggestions, keywordSuggestions, faq_entries_stream,
}: Props) {
  const [selectedId, setSelectedId] = useState<number | null>(faqEntries[0]?.id ?? null)
  const [isDraft, setIsDraft] = useState(false)
  const [search, setSearch] = useState("")
  const [deleteCandidate, setDeleteCandidate] = useState<FaqEntry | null>(null)
  const [deleting, setDeleting] = useState(false)

  useInertiaCable(faq_entries_stream, { only: ["faqEntries"] })

  const selected = useMemo(() => {
    if (isDraft) return null
    return faqEntries.find((entry) => entry.id === selectedId) ?? null
  }, [faqEntries, selectedId, isDraft])

  useEffect(() => {
    if (!isDraft && selectedId && !faqEntries.find((entry) => entry.id === selectedId)) {
      setSelectedId(faqEntries[0]?.id ?? null)
    }
  }, [faqEntries, selectedId, isDraft])

  const handleAgentChange = (agentId: number) => {
    router.get("/faq_entries", { agent_id: agentId }, { preserveScroll: true, preserveState: false })
  }

  const startNew = () => {
    setIsDraft(true)
    setSelectedId(DRAFT_ID)
  }

  const handleSelect = (id: number) => {
    setIsDraft(false)
    setSelectedId(id)
  }

  const handleSaved = () => {
    setIsDraft(false)
    if (selectedId === DRAFT_ID) setSelectedId(null)
  }

  const handleCancel = () => {
    setIsDraft(false)
    setSelectedId(faqEntries[0]?.id ?? null)
  }

  const handleDelete = () => {
    if (!selected) return
    setDeleteCandidate(selected)
  }

  const confirmDelete = () => {
    if (!deleteCandidate) return
    setDeleting(true)
    router.delete(`/faq_entries/${deleteCandidate.id}`, {
      preserveScroll: true,
      onSuccess: () => { setSelectedId(null) },
      onFinish: () => { setDeleting(false); setDeleteCandidate(null) },
    })
  }

  return (
    <AppShell>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <PageHeader
            eyebrow="Content"
            title="FAQ Entries"
            subtitle="Manage canonical answers, topics, and lookup tags."
            actions={<Button onClick={startNew} className="w-full sm:w-auto">+ New FAQ entry</Button>}
          />
        </div>
        <div className="hidden pt-9 md:block">
          <HelpHint
            heading="What are FAQ entries?"
            body={
              <>
                <p>FAQ entries are canonical answer bodies your agent can fetch with the lookup_faq tool.</p>
                <p className="mt-2">Topics and tags help the agent pick the right entry before it reads the full answer.</p>
              </>
            }
          />
        </div>
      </div>

      <section className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:h-[calc(100vh-220px)] lg:min-h-[640px] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] lg:overflow-hidden">
        <FaqEntryList
          agents={agents}
          selectedAgentId={selectedAgentId}
          onAgentChange={handleAgentChange}
          faqEntries={faqEntries}
          selectedId={selectedId}
          onSelect={handleSelect}
          search={search}
          onSearchChange={setSearch}
        />

        {selectedId !== null ? (
          <FaqEntryForm
            faqEntry={selected}
            agentId={selectedAgentId}
            topicSuggestions={topicSuggestions}
            keywordSuggestions={keywordSuggestions}
            isDraft={isDraft}
            onSaved={handleSaved}
            onCancel={handleCancel}
            onDelete={handleDelete}
          />
        ) : (
          <div className="flex items-center justify-center rounded-card border border-border bg-app-panel p-6 text-sm text-app-muted">
            Select a FAQ entry or click <span className="mx-1 font-medium text-app-text">+ New FAQ entry</span> to start.
          </div>
        )}
      </section>

      <Dialog open={deleteCandidate !== null} onClose={() => !deleting && setDeleteCandidate(null)} size="sm">
        <DialogHeader title="Delete FAQ entry?" subtitle="This cannot be undone." />
        <DialogBody>
          <p className="text-sm text-app-text">
            Permanently delete <span className="font-medium">"{deleteCandidate?.name}"</span>?
            The agent will no longer be able to fetch this answer.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setDeleteCandidate(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={confirmDelete}
            disabled={deleting}
            className="border border-status-error bg-status-error/90 hover:bg-status-error"
          >
            {deleting ? "Deleting..." : "Delete FAQ entry"}
          </Button>
        </DialogFooter>
      </Dialog>
    </AppShell>
  )
}
